// frontend/src/components/jobPlacement/JobPlacementPage.jsx
import React, { useState, useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import JobPlacementForm from "./JobPlacementForm";
import JobPlacementList from "./JobPlacementList";
import JobPlacementView from "./JobPlacementView";
import JobPlacementRelatedForms from "./JobPlacementRelatedForms";

const JobPlacementPage = () => {
  const { tenant_id, user, role } = useContext(AuthContext);
  const user_id = user?.user_id;
  const [searchParams, setSearchParams] = useSearchParams();
  const apiBase = getApiBaseURL();

  const [activeTab, setActiveTab] = useState("list");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [relatedFormsPlan, setRelatedFormsPlan] = useState(null);
  const [initialFollowupId, setInitialFollowupId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showPHINotice, setShowPHINotice] = useState(() => {
    return sessionStorage.getItem("jobPlacementPhiNoticeDismissed") !== "true";
  });
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Handle URL-based deep linking for followup editing
  useEffect(() => {
    const loadFromUrlParams = async () => {
      if (initialLoadDone) return;

      const jobPlacementId = searchParams.get("job_placement_id");
      const followupId = searchParams.get("followup_id");
      const action = searchParams.get("action");

      if (jobPlacementId && tenant_id) {
        try {
          // Fetch the job placement data
          const res = await secureAxios.get(
            `${apiBase}/api/job-placements/${jobPlacementId}`,
            { params: { tenant_id } }
          );

          if (res.data) {
            const plan = res.data;

            // Also fetch consumer data
            if (plan.consumer_id) {
              const consumerRes = await secureAxios.get(
                `${apiBase}/api/consumers/${plan.consumer_id}`,
                { params: { tenant_id } }
              );
              plan.consumer = consumerRes.data;
            }

            // Navigate to related forms (which includes follow-up editing)
            if (action === "edit-followup" || followupId) {
              setRelatedFormsPlan(plan);
              setInitialFollowupId(followupId); // Pass the followup ID to auto-open the form
              setActiveTab("related");
            } else {
              setViewingPlan(plan);
              setActiveTab("view");
            }
          }
        } catch (err) {
          console.error("Error loading job placement from URL:", err);
        }
      }

      setInitialLoadDone(true);
    };

    loadFromUrlParams();
  }, [searchParams, tenant_id, apiBase, initialLoadDone]);

  // Clear URL params when navigating away
  const clearUrlParams = () => {
    if (
      searchParams.has("job_placement_id") ||
      searchParams.has("followup_id")
    ) {
      setSearchParams({});
    }
    setInitialFollowupId(null);
  };

  const handleEdit = (plan) => {
    clearUrlParams();
    setSelectedPlan(plan);
    setViewingPlan(null);
    setRelatedFormsPlan(null);
    setActiveTab("form");
  };

  const handleView = (plan) => {
    clearUrlParams();
    setViewingPlan(plan);
    setSelectedPlan(null);
    setRelatedFormsPlan(null);
    setActiveTab("view");
  };

  const handleRelatedForms = (plan) => {
    clearUrlParams();
    setRelatedFormsPlan(plan);
    setSelectedPlan(null);
    setViewingPlan(null);
    setActiveTab("related");
  };

  const handleCreate = () => {
    clearUrlParams();
    setSelectedPlan(null);
    setViewingPlan(null);
    setRelatedFormsPlan(null);
    setActiveTab("form");
  };

  const handleDone = () => {
    clearUrlParams();
    setSelectedPlan(null);
    setViewingPlan(null);
    setRelatedFormsPlan(null);
    setActiveTab("list");
    setRefreshKey((prev) => prev + 1);
  };

  const handleBackToList = () => {
    clearUrlParams();
    setSelectedPlan(null);
    setViewingPlan(null);
    setRelatedFormsPlan(null);
    setActiveTab("list");
  };

  const handleDismissPHINotice = () => {
    setShowPHINotice(false);
    sessionStorage.setItem("jobPlacementPhiNoticeDismissed", "true");
  };

  return (
    <div className="container-fluid px-2 px-md-4 mt-3 mt-md-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 mb-md-3 gap-2">
        <h4 className="mb-0 h5">
          <i className="bi bi-clipboard-check me-2"></i>
          CRP-30 Job Placement Plan
        </h4>
        <span className="badge bg-warning text-dark">
          <i className="bi bi-shield-lock me-1"></i>
          HIPAA Protected
        </span>
      </div>

      {/* PHI Notice - Dismissible */}
      {showPHINotice && (
        <div
          className="alert alert-info alert-dismissible fade show mb-3"
          role="alert"
        >
          <h6 className="alert-heading">
            <i className="bi bi-info-circle-fill me-2"></i>
            Protected Health Information Notice
          </h6>
          <small>
            This module contains Protected Health Information (PHI). All access
            and actions are monitored and logged for HIPAA compliance. Only
            authorized personnel should access consumer job placement plans.
          </small>
          <button
            type="button"
            className="btn-close"
            onClick={handleDismissPHINotice}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs flex-nowrap overflow-auto mb-2 mb-md-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={handleBackToList}
          >
            <i className="bi bi-list-ul me-2"></i>
            All Plans
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "form" ? "active" : ""}`}
            onClick={handleCreate}
          >
            <i className="bi bi-plus-circle me-2"></i>
            {selectedPlan ? "Edit Plan" : "Create Plan"}
          </button>
        </li>
        {viewingPlan && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "view" ? "active" : ""}`}
              onClick={() => setActiveTab("view")}
            >
              <i className="bi bi-eye me-2"></i>
              View Plan
            </button>
          </li>
        )}
        {relatedFormsPlan && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "related" ? "active" : ""}`}
              onClick={() => setActiveTab("related")}
            >
              <i className="bi bi-files me-2"></i>
              CRP-31/32 Forms
            </button>
          </li>
        )}
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "list" && (
          <JobPlacementList
            key={refreshKey}
            tenant_id={tenant_id}
            user_id={user_id}
            role={role}
            onEdit={handleEdit}
            onView={handleView}
            onRelatedForms={handleRelatedForms}
          />
        )}

        {activeTab === "form" && (
          <JobPlacementForm
            tenant_id={tenant_id}
            user_id={user_id}
            role={role}
            plan={selectedPlan}
            onSave={handleDone}
            onCancel={handleBackToList}
          />
        )}

        {activeTab === "view" && viewingPlan && (
          <JobPlacementView
            plan={viewingPlan}
            tenantId={tenant_id}
            onBack={handleBackToList}
            onEdit={() => handleEdit(viewingPlan)}
            onRelatedForms={() => handleRelatedForms(viewingPlan)}
          />
        )}

        {activeTab === "related" && relatedFormsPlan && (
          <JobPlacementRelatedForms
            jobPlacement={relatedFormsPlan}
            consumer={{
              consumer_id: relatedFormsPlan.consumer_id,
              name: relatedFormsPlan.consumer_name,
            }}
            onBack={handleBackToList}
            initialFollowupId={initialFollowupId}
          />
        )}
      </div>

      {/* Security Footer - matching MonthlyReportsPage pattern */}
      <div className="mt-3 mt-md-4 p-2 p-md-3 bg-light rounded d-none d-md-block">
        <h6 className="mb-2">
          <i className="bi bi-shield-check me-2"></i>
          Security Information
        </h6>
        <small className="text-muted">
          <ul className="mb-0">
            <li>All data is encrypted using AES-256 encryption</li>
            <li>Access logs are maintained for audit purposes</li>
            <li>
              Electronic signatures are legally binding under the E-Sign Act
            </li>
            <li>Reports are stored in HIPAA-compliant cloud storage</li>
          </ul>
        </small>
      </div>
    </div>
  );
};

export default JobPlacementPage;